import router from '@/router';
import { defineStore } from 'pinia';
import { fetchProspects, fetchEvents, addEvent, updateEvent, deleteEvent, fetchPeople, fetchContacts, addContact, editContact, deleteContact, fetchPageviews, fetchSessions, updateProspect } from '@/services/ppApi';
import { useAccountStore } from '@/stores/account';
import { useUserStore } from '@/stores/user';
import { useSearchHistoryStore } from '@/stores/searchHistory';

export const useProspectStore = defineStore('prospect', {
    state: () => ({
        prospect: null,
        events: [],
        people: [],
        contacts: [],
        sessions: [],
        pageviews: [],
        apps: null,
        industries: null,
        loading: false,
        reviewsDetails: null,
        reviewsSummary: null,
        location: null,
        openingHours: null,
        searchOptions: null,
        profile: null,
        property: null,
        siterank: null,
        sitesearch: null,
        vacancies: null,
        news: null,
        peopleDetails: null,
        vat: null,
        family: null,
        sameAddress: null,
        ai: {}
    }),
    actions: {
        async fetchProspect(id) {
            this.loading = true;

            try {
                const accountStore = useAccountStore();

                const response = await fetchProspects({
                    add_details: '*',
                    add_fields: 'owner',
                    account_id: accountStore.account.account_id,
                    company: id
                });

                if(response.data.found && response.data.found > 0) {
                    this.setProspect(response.data.companies[0]);
                } else {
                    this.resetProspect();
                    router.push({ name: process.env.VUE_APP_DEFAULT_ROUTE });
                }
                return true;
            } catch {
                router.push({ name: process.env.VUE_APP_DEFAULT_ROUTE });
                return false;
            } finally {
                this.loading = false;
            }
        },
        async updateProspect(data, refresh = false) {
            const accountStore = useAccountStore();
            const payload = {
                id: this.prospect.id,
                account_id: accountStore.account.account_id,
                ...data
            }
            try {
                const response = await updateProspect(payload);
                if(response.data.found && response.data.found > 0) {
                    if(refresh) {
                        this.setProspect(response.data.companies[0]);
                        return true;
                    } else {
                        return response.data.companies[0];
                    }
                } else {
                    return false;
                }
            } catch {
                return false;
            }
        },
        async updateOwner(id) {
            try {
                const company = await this.updateProspect({uid: parseInt(id)});
                if(company) {
                    this.prospect.owner = company.owner || '';
                    this.prospect.owner_id = company.owner_id || '';
                    this.prospect.changed_time = company.changed_time || 0;
                    return true;
                } else {
                    return false;
                }
            } catch {
                return false;
            }
        },
        async updateTags(tags) {
            try {
                const company = await this.updateProspect({tags});
                if(company) {
                    this.prospect.tags = company.tags || '';
                    this.prospect.changed_time = company.changed_time || 0;
                    return true;
                } else {
                    return false;
                }
            } catch {
                return false;
            }
        },
        async updateLabel(label) {
            try {
                const company = await this.updateProspect({label});
                if(company) {
                    this.prospect.label = company.label;
                    this.prospect.changed_time = company.changed_time || 0;
                    return true;
                } else {
                    return false;
                }
            } catch {
                return false;
            }
        },
        async fetchContacts(signal) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await fetchContacts({
                    account_id: accountStore.account.account_id,
                    company_id: this.prospect.id
                }, signal);
                if(response.data.found && response.data.found > 0) {
                    this.contacts = response.data.contacts;
                } else {
                    this.contacts = [];
                }
            } catch {
                this.contacts = [];
            }
        },
        async addContact(contact) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await addContact({
                    account_id: accountStore.account.account_id,
                    company_id: this.prospect.id,
                    ...contact
                });
                if(response.data.found && response.data.found > 0) {
                    this.updateContactList(response.data.contacts[0]);
                    this.updatePeopleList(response.data.contacts[0], true);
                }
                return true;
            } catch {
                return false;
            }
        },
        async editContact(contact) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await editContact({
                    account_id: accountStore.account.account_id,
                    company_id: this.prospect.id,
                    ...contact
                });
                if(response.data.found && response.data.found > 0) {
                    this.updateContactList(response.data.contacts[0]);
                }
                return true;
            } catch {
                return false;
            }
        },
        async deleteContact(contact) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await deleteContact({
                    account_id: accountStore.account.account_id,
                    company_id: this.prospect.id,
                    ...contact
                });
                if(response.data.status && response.data.status === 'ok') {
                    this.updateContactList(contact, true);
                }
                return true;
            } catch {
                return false;
            }
        },
        updateContactList(contact, deleteContact = false) {
            const existingIndex = this.contacts.findIndex(c => c.contact_id === contact.contact_id);
            if (existingIndex !== -1) {
                if(deleteContact) {
                    this.contacts.splice(existingIndex, 1);
                } else {
                    this.contacts.splice(existingIndex, 1, contact);
                }
            } else {
                if(!deleteContact) {
                    this.contacts.push(contact);
                }
            }
        },
        async fetchEvents(signal) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await fetchEvents({
                    account_id: accountStore.account?.account_id || '',
                    id: this.prospect.id
                }, signal);
                
                if((response.data.found && response.data.found > 0) || response.data.status === 'ok') {
                    this.events = response.data.events;
                } else {
                    this.events = [];
                }
            } catch(err) {
                this.events = [];
            }
        },
        async addEvent(event) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await addEvent({
                    account_id: accountStore.account.account_id,
                    id: this.prospect.id,
                    ...event
                });
                if(response.data.found && response.data.found > 0) {
                    this.updateEventList(response.data.events[0]);
                }
                return true;
            } catch {
                return false;
            }
        },
        async editEvent(event) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await updateEvent({
                    account_id: accountStore.account.account_id,
                    id: this.prospect.id,
                    ...event
                });
                if(response.data.found && response.data.found > 0) {
                    this.updateEventList(response.data.events[0]);
                }
                return true;
            } catch {
                return false;
            }
        },
        async deleteEvent(event) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await deleteEvent({
                    account_id: accountStore.account.account_id,
                    id: this.prospect.id,
                    ...event
                });
                if(response.data.status && response.data.status === 'ok') {
                    this.updateEventList(event, true);
                }
                return true;
            } catch {
                return false;
            }
        },
        updateEventList(event, deleteEvent = false) {
            const existingIndex = this.events.findIndex(c => c.event_id === event.event_id);
            if (existingIndex !== -1) {
                if(deleteEvent) {
                    this.events.splice(existingIndex, 1);
                } else {
                    this.events.splice(existingIndex, 1, event);
                }
            } else {
                if(!deleteEvent) {
                    this.events.push(event);
                }
            }
        },
        async fetchPeople(text, signal) {
            if(!this.prospect || !this.prospect.id) { return }

            const isAuthenticated = useUserStore().isAuthenticated;

            let id;
            if (process.env.VUE_APP_TYPE === 'search' && !isAuthenticated && this.prospect.ch) {
                id = { id: this.prospect.id + '_' + this.prospect.ch };
            } else {
                id = { company_id: this.prospect.id };
            }

            try {
                const accountStore = useAccountStore();
                const response = await fetchPeople({
                    account_id: accountStore.account?.account_id || '',
                    limit: '100',
                    ...(text && { text }),
                    ...(id && id)
                }, signal);
                
                if(response.data.found && response.data.found > 0) {
                    this.people = response.data.people;
                } else {
                    this.people = [];
                }
            } catch(err) {
                this.people = [];
            }
        },
        updatePeopleList(contact, deletePerson = false) {
            const existingIndex = this.people.findIndex(c => c.contact_id === contact.contact_id);
            if (existingIndex !== -1) {
                if(deletePerson) {
                    this.people.splice(existingIndex, 1);
                } else {
                    this.people.splice(existingIndex, 1, contact);
                }
            } else {
                if(!deletePerson) {
                    this.people.push(contact);
                }
            }
        },
        async fetchSessions(signal) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await fetchSessions({
                    account_id: accountStore.account.account_id,
                    id: this.prospect.id,
                    group: 'session',
                    add_logo: 1
                }, signal);
                if(response.data.found && response.data.found > 0) {
                    this.sessions = response.data.sessions;
                } else {
                    this.sessions = [];
                }
            } catch {
                this.sessions = [];
            }
        },
        async fetchPageviews(signal) {
            if(!this.prospect || !this.prospect.id) { return }
            try {
                const accountStore = useAccountStore();
                const response = await fetchPageviews({
                    account_id: accountStore.account.account_id,
                    id: this.prospect.id,
                    sources: 1
                }, signal);
                if(response.data.found && response.data.found > 0) {
                    this.pageviews = response.data.pageviews;
                } else {
                    this.pageviews = [];
                }
            } catch {
                this.pageviews = [];
            }
        },
        setProspect(prospect) {
            const details = prospect?.details || {};
            const aiOptions = [
                                    'fundamentals_nl',
                                    'fundamentals_en',
                                    'summary_nl',
                                    'summary_en'
                                ];
            this.people = [];
            this.peopleDetails = details?.people || null;
            this.events = [];
            this.contacts = [];
            this.sessions = [];
            this.pageviews = [];
            this.apps = details?.apps || null;
            this.industries = details?.industries || null;
            this.reviewsDetails = details?.reviews?.reviews || null;
            this.reviewsSummary = details?.reviews?.summary || null;
            this.location = details?.map || null;
            this.openingHours = details?.open?.open_structured || null;
            this.searchOptions = details?.similar_search || null;
            this.profile = details?.profile || null;
            this.property = details?.property || null;
            this.siterank = details?.siterank || null;
            this.sitesearch = details?.sitesearch || null;
            this.vacancies = details?.vacancies || null;
            this.news = details?.news || null;
            this.vat = details?.vat || null;
            this.family = details?.family || null;
            this.sameAddress = details?.same_address || null;
            this.ai = {};

            aiOptions.forEach((key) => {
                this.ai[key] = details[key] || null;
            });

            this.prospect = {...prospect};
            useSearchHistoryStore().add(prospect);
        },
        resetProspect() {
            this.$reset();
        },
        deleteAiOption(slug) {
            if(!this.ai[slug]) {
                return;
            } else {
                delete this.ai[slug];
            }
        }
    }
});